import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBillingPoint, DineServableOutlet } from 'api/types';

/**
 * Add / edit a cash counter of the header outlet (DO-313, spec 2.40 §12.1a).
 * Sections are given a counter on the Sections screen; the default takes every
 * section without one, and room service, takeaway and counter orders.
 * "Also serves outlets" makes it a shared counter (S1): those outlets' bills
 * come here instead of to their own default, and its shift takes their money.
 */
interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DineBillingPoint | null;
  outletName: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FormData { code: string; name: string; printer: string; order: string; isDefault: boolean; isActive: boolean; served: string[] }
const EMPTY: FormData = { code: '', name: '', printer: '', order: '10', isDefault: false, isActive: true, served: [] };

const DineOpsBillingPointFormModal: React.FC<Props> = ({ isOpen, mode, record, outletName, onClose, onSaved }) => {
  const [formData, setFormData] = useState<FormData>(EMPTY);
  const snapshot = useRef('');
  const recordRef = useRef(record);
  recordRef.current = record;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [servable, setServable] = useState<DineServableOutlet[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    dineOpsService.getServableOutlets().then(setServable).catch(() => setServable([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const r = recordRef.current;
    const data: FormData = mode === 'edit' && r
      ? { code: r.billingPointCode, name: r.billingPointName, printer: r.printerName ?? '', order: String(r.displayOrder), isDefault: r.isDefault, isActive: r.isActive,
          served: [...(r.servedOutletIds ?? [])].sort() }
      : { ...EMPTY };
    setFormData(data);
    snapshot.current = JSON.stringify(data);
    setFieldErrors({});
    setServerError(null);
  }, [isOpen, mode]);

  const isDirty = useMemo(() => !!snapshot.current && JSON.stringify(formData) !== snapshot.current, [formData]);
  const wasDefault = mode === 'edit' && !!record?.isDefault;

  const set = (key: keyof FormData, value: string | boolean) => {
    setFieldErrors((p) => { if (!p[key]) return p; const n = { ...p }; delete n[key]; return n; });
    setFormData((p) => ({ ...p, [key]: value }));
  };

  const toggleServed = (outletId: string, on: boolean) =>
    setFormData((p) => ({ ...p, served: (on ? [...p.served, outletId] : p.served.filter((x) => x !== outletId)).sort() }));

  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const e: Record<string, string> = {};
    if (!/^[A-Za-z0-9_-]{1,20}$/.test(formData.code.trim())) e.code = 'The code is 1 to 20 letters, digits, - or _, e.g. C1.';
    if (!formData.name.trim()) e.name = 'The counter needs a name, e.g. Counter 1.';
    if (formData.order.trim() && !Number.isInteger(Number(formData.order))) e.order = 'A whole number.';
    if (Object.keys(e).length) { setFieldErrors(e); return; }
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    setServerError(null);
    const body = {
      billingPointCode: formData.code.trim().toUpperCase(),
      billingPointName: formData.name.trim(),
      printerName: formData.printer.trim() || undefined,
      displayOrder: Number(formData.order) || 0,
      isDefault: formData.isDefault,
      servedOutletIds: formData.served,
    };
    try {
      if (mode === 'edit' && record) {
        await dineOpsService.updateBillingPoint(record.id, { ...body, isActive: formData.isActive, rowVersion: record.rowVersion });
        notificationService.toast.success(`"${body.billingPointName}" updated.`);
      } else {
        await dineOpsService.createBillingPoint(body);
        notificationService.toast.success(`"${body.billingPointName}" added to ${outletName}. Give sections this counter on the Sections screen.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      setServerError(err?.response?.data?.message ?? 'That could not be saved.');
    }
  }, [formData, mode, record, outletName, onSaved, onClose]);

  if (!isOpen) return null;
  const err = (k: string) => fieldErrors[k];

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 620 }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? `Add Counter — ${outletName}` : `Edit Counter — ${record?.billingPointName ?? ''}`}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
          <div className="form-grid-2">
            <div className="form-group" data-field="code">
              <label className="form-label" htmlFor="bp-code">Code <span className="text-danger">*</span></label>
              <input id="bp-code" className={`form-input${err('code') ? ' form-field-error' : ''}`} value={formData.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="e.g., C1" maxLength={20} />
              {err('code') && <div className="form-error-message">{err('code')}</div>}
            </div>
            <div className="form-group" data-field="name">
              <label className="form-label" htmlFor="bp-name">Name <span className="text-danger">*</span></label>
              <input id="bp-name" className={`form-input${err('name') ? ' form-field-error' : ''}`} value={formData.name}
                onChange={(e) => set('name', e.target.value)} placeholder="e.g., Counter 1" maxLength={100} />
              {err('name') && <div className="form-error-message">{err('name')}</div>}
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-group" data-field="printer">
              <label className="form-label" htmlFor="bp-printer">Invoice printer</label>
              <input id="bp-printer" className="form-input" value={formData.printer} onChange={(e) => set('printer', e.target.value)} placeholder="Printer name" />
            </div>
            <div className="form-group" data-field="order">
              <label className="form-label" htmlFor="bp-order">Display order</label>
              <input id="bp-order" type="number" className={`form-input${err('order') ? ' form-field-error' : ''}`} value={formData.order}
                onChange={(e) => set('order', e.target.value)} />
              {err('order') && <div className="form-error-message">{err('order')}</div>}
            </div>
          </div>
          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={formData.isDefault} disabled={wasDefault} onChange={(e) => set('isDefault', e.target.checked)} />
              <span>Default counter of {outletName}</span>
            </label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {wasDefault
                ? 'This is the default. To change it, make another counter the default.'
                : 'The default takes sections without a counter, room service, takeaway and counter orders. Ticking this replaces the current default.'}
            </div>
            {mode === 'edit' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.isActive} disabled={wasDefault} onChange={(e) => set('isActive', e.target.checked)} />
                <span>Active</span>
              </label>
            )}
          </div>
          {servable.length > 0 && (
            <div className="form-group" data-field="served">
              <label className="form-label">Also serves outlets</label>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                A shared counter: the ticked outlets' bills come here instead of to their own default counter, and the
                shift at this counter takes their money. Use it where one cashier and one cash drawer serve several outlets.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {servable.map((o) => {
                  const elsewhere = !!o.servedByBillingPointId && o.servedByBillingPointId !== record?.id;
                  return (
                    <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, color: elsewhere ? 'var(--text-muted)' : undefined }}>
                      <input type="checkbox" checked={formData.served.includes(o.id)} disabled={elsewhere}
                        onChange={(e) => toggleServed(o.id, e.target.checked)} />
                      <span>{o.outletName} ({o.outletCode}){elsewhere ? ` — served by ${o.servedByName}` : ''}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}>Save</button>
        </div>
      </div>
      <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
        message={mode === 'create' ? `Add the counter "${formData.name.trim()}"?` : `Save the changes to "${formData.name.trim()}"?`}
        confirmLabel="Confirm" cancelLabel="Cancel" variant="default" onConfirm={() => void handleConfirmSave()} onCancel={() => setSaveConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

export default DineOpsBillingPointFormModal;
